import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-book-read-line text-lg text-white"></i>
            </div>
            <span className={`text-xl font-bold font-heading whitespace-nowrap ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-white'
            }`}>
              韩漫网
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isHome
                  ? isScrolled
                    ? 'text-foreground-950 hover:bg-background-200'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                  : 'text-foreground-950 hover:bg-background-200'
              }`}
            >
              首页
            </Link>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  isScrolled || !isHome
                    ? 'text-foreground-950 hover:bg-background-200'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCategoryOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden z-20 py-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="w-7 h-7 flex items-center justify-center">
                          <i className={`${cat.icon} text-base text-foreground-600`}></i>
                        </div>
                        <span className="text-sm text-foreground-800 flex-1">{cat.name}</span>
                        <span className="text-xs text-foreground-400">{cat.count}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/category/completed"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled || !isHome
                  ? 'text-foreground-950 hover:bg-background-200'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              已完结
            </Link>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled || !isHome
                  ? 'text-foreground-950 hover:bg-background-200'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Search + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <button
              className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                isScrolled || !isHome
                  ? 'hover:bg-background-200 text-foreground-600'
                  : 'hover:bg-white/10 text-white/80'
              }`}
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                isScrolled || !isHome
                  ? 'hover:bg-background-200 text-foreground-600'
                  : 'hover:bg-white/10 text-white/80'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4">
          <div className="flex flex-col gap-1">
            <Link to="/" className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">首页</Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer flex items-center gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm text-foreground-500`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">关于我们</Link>
          </div>
        </div>
      )}
    </nav>
  );
}